import React, { useEffect, useState } from "react";
import styles from "./HomePage.module.css";
import NavBar from "../../components/NavBar/NavBar";
import filterIcon from "../../assets/filter.png";  
import FilterSheet from "../../components/FilterSheet/FilterSheet";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../App";


// ICONS
import analyticsIcon from "../../assets/analytics.png";
import viewsIcon from "../../assets/views.png";
import commentsIcon from "../../assets/comments.png";
import clickIcon from "../../assets/click.png";

export default function HomePage() {
  const [globalLists, setGlobalLists] = useState([]);
  const [displayLists, setDisplayLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null); 
  const [scope, setScope] = useState("public");
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedListing, setSelectedListing] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null); 


  const [filters, setFilters] = useState({
    minPrice: "",
    maxPrice: "",
    bedrooms: "",
    bathrooms: "",
    minSqft: "",
    maxSqft: "",
    utils: [],
    tags: [],
    customUtil: "",
    customTag: "",
  });

  const API_URL = `${BASE_URL}backend/get_homepage.php`;
  const navigate = useNavigate();

  // ---------------- AUTO-DISMISS ERROR ----------------
  useEffect(() => {
    if (fetchError) {
      const timer = setTimeout(() => {
        setFetchError(null);
      }, 5000); // Clear error after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [fetchError]);

  // ---------------- FETCH LISTINGS & CURRENT USER ----------------
  useEffect(() => {
    let cancelled = false;
    setFetchError(null); 

    // Fetch current logged-in user ID
    fetch(`${BASE_URL}backend/get_user_info.php`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) {
            setCurrentUserId(d.user_id || null);
        }
      })
      .catch((err) => console.error("Could not fetch user info:", err));
      
    async function fetchListings() {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}?scope=${scope}&_=${Date.now()}`
        );

        if (response.status === 401) {
            throw new Error("Invalid session. Please log in again");
        }

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        const rawListings = Array.isArray(data)
          ? data
          : Array.isArray(data.listings)
          ? data.listings
          : Array.isArray(data.data)
          ? data.data
          : [];

        const fetched = rawListings.map((item) => {
          const imageArray = Array.isArray(item.image_paths)
            ? item.image_paths
            : [];

          const firstImage = imageArray[0] || item.img || item.image || "";

          const imgSrc =
            !firstImage
              ? ""
              : firstImage.startsWith("http") || firstImage.startsWith("/")
              ? firstImage
              : `${BASE_URL}${firstImage}`;

          return {
            id: item.post_id,
            authorId: String(item.user_id || item.creator || item.owner_id || ''),
            img: imgSrc,
            title: item.title || "",
            price: Number(item.rent !== undefined ? item.rent : item.price || 0),
            bedrooms: parseInt(item.bed !== undefined ? item.bed : item.bedrooms || 0),
            bathrooms: parseInt(item.bath !== undefined ? item.bath : item.bathrooms || 0),
            address: item.addr || item.address || "",
            sqft: Number(item.sqFT !== undefined ? item.sqFT : item.sqft || 0),
            utils: Array.isArray(item.utils)
              ? item.utils.map((u) => String(u).toLowerCase())
              : String(item.utils || "").split(",").map((u) => u.trim().toLowerCase()).filter((u) => u.length > 0),
            tags: Array.isArray(item.tags)
              ? item.tags.map((t) => String(t).toLowerCase())
              : String(item.tags || "").split(",").map((t) => t.trim().toLowerCase()).filter((t) => t.length > 0),
            pageLink: item.post_id,
            closed: item.closed == 1, 
          };
        });

        if (!cancelled) {
          setGlobalLists(fetched);
          setDisplayLists(fetched);
        }
      } catch (err) {
        console.error("HomePage fetch error:", err);
        if (!cancelled) {
          setFetchError(err.message || "Failed to fetch listings.");
          setGlobalLists([]);
          setDisplayLists([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchListings();
    return () => {
      cancelled = true;
    };
  }, [scope, API_URL]);


  // ---------------- FILTER + SEARCH ----------------
  const applyFiltersAndSearch = (searchQuery, appliedFilters) => {
    // If there is an error or no data, we don't need to filter
    if (!globalLists.length) {
        setDisplayLists([]);
        return;
    }
    
    const q = searchQuery.toLowerCase();

    const results = globalLists.filter((listing) => {
      const price = listing.price || 0;
      const beds = listing.bedrooms || 0;
      const baths = listing.bathrooms || 0;
      const sqft = listing.sqft || 0;

      const {
        minPrice, maxPrice, bedrooms, bathrooms, minSqft, maxSqft, utils, tags,
      } = appliedFilters;

      if (minPrice && price < minPrice) return false;
      if (maxPrice && price > maxPrice) return false;
      if (bedrooms && beds !== parseInt(bedrooms)) return false;
      if (bathrooms && baths !== parseInt(bathrooms)) return false;
      if (minSqft && sqft < minSqft) return false;
      if (maxSqft && sqft > maxSqft) return false;

      if (utils.length > 0) {
        if (!utils.every((u) => listing.utils.includes(u.toLowerCase()))) return false;
      }
      if (tags.length > 0) {
        if (!tags.every((t) => listing.tags.includes(t.toLowerCase()))) return false;
      }
      if (q) {
  const addr = listing.address ? listing.address.toLowerCase() : "";

  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // -----------------------------------------
  // CASE 1: USER SEARCHED NUMBERS ONLY
  // -----------------------------------------
  if (/^\d+$/.test(q)) {
    const firstToken = addr.split(/\s+/)[0]; // house number only

    // must start with the digits typed
    const numRegex = new RegExp(`^${safe}`, "i");

    if (!numRegex.test(firstToken)) return false;

    return true;
  }

  // -----------------------------------------
  // CASE 2: USER SEARCHED LETTERS
  // -----------------------------------------

  // Split all words
  const words = addr.split(/\s+/);

  // Find FIRST word beginning with a letter (skip house numbers)
  const firstRealWord = words.find(w => /^[a-z]/i.test(w)) || "";

  // must start with the letters typed
  const letterRegex = new RegExp(`^${safe}`, "i");

  if (!letterRegex.test(firstRealWord)) return false;
}


      return true;
    });

    setDisplayLists(results);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFiltersAndSearch(value, filters);
  };

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    applyFiltersAndSearch(searchTerm, appliedFilters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const cleared = {
      minPrice: "", maxPrice: "", bedrooms: "", bathrooms: "", minSqft: "", maxSqft: "",
      utils: [], tags: [], customUtil: "", customTag: "",
    };
    setFilters(cleared);
    applyFiltersAndSearch(searchTerm, cleared);
  };

  // ---------------- CARD CLICK ----------------
  const handleCardClick = async (listing) => {
    const isOwner = currentUserId && (String(currentUserId) === listing.authorId);

    if (listing.closed && !isOwner) {
      console.log(`Listing ${listing.pageLink} is closed. Navigation blocked for non-owner.`);
      return; 
    }
    
    try {
      await fetch(`${BASE_URL}backend/track_event.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "listing_click",
          post_id: listing.pageLink,
        }),
      });
    } catch (err) {
      console.error("Track error:", err);
    } finally {
      navigate(`/houseListing/${listing.pageLink}`);
    }
  };

  // ---------------- RENDER ----------------
  return (
    <div className={styles.page}>
      <NavBar />

      <header className={styles.header}>
        <div className={styles.searchBarWrap}>
          <input
            className={styles.searchBar}
            type="text"
            placeholder="Search by address"
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={!!fetchError} 
          />
        </div>

        <div className={styles.topRight}>
          <button
            className={styles.filterButton}
            onClick={() => setShowFilters(true)}
            aria-label="Filters"
            disabled={!!fetchError} 
          >
            <img src={filterIcon} alt="" className={styles.filterIcon} />
          </button>


          <div className={styles.scopeToggle}>
            <button
              className={scope === "public" ? styles.scopeButtonActive : styles.scopeButton}
              onClick={() => setScope("public")}
              disabled={!!fetchError && scope !== "public"}
            >
              Public Listings
            </button>
            <button
              className={scope === "mine" ? styles.scopeButtonActive : styles.scopeButton}
              onClick={() => setScope("mine")}
              disabled={!!fetchError && scope !== "mine"}
            >
              My Listings
            </button>
          </div>
        </div>
      </header>

      {showFilters && (
        <FilterSheet
          filters={filters}
          onClose={() => setShowFilters(false)}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      )}

      {/* LISTINGS */}
      {isLoading ? (
        <div className="container">Loading…</div>
      ) : displayLists.length === 0 ? (
        <div className="container">No listings found.</div>
      ) : (
        <section className={styles.grid}>
          {displayLists.map((l, i) => (
            <article
              key={l.id || i}
              className={styles.card}
              onClick={() => handleCardClick(l)}
            >
              <div className={styles.cardTitleTop}>
                {l.title || `${l.bedrooms} bed, ${l.bathrooms} bath apartment`}
              </div>

              <div className={styles.imageWrap}>
                <img 
                  src={l.img} 
                  alt="Listing" 
                  className={l.closed ? styles.grayscaleClosed : ''} 
                />
                {l.closed && (
                  <div className={styles.closedOverlay}>CLOSED</div>
                )}
              </div>

              <div className={styles.meta}>
                <div className={styles.price}>${l.price}/month</div>

                <div className={styles.bedsBaths}>
                  {l.bedrooms} bed, {l.bathrooms} bath apartment
                </div>

                <div className={styles.subtitleRow}>
                  <div className={styles.subtitle}>{l.address}</div>
                  {scope === "mine" && (
                    <button
                      className={styles.analyticsBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedListing(l);
                      }}
                      title="View Analytics"
                    >
                      <img src={analyticsIcon} alt="Analytics" />
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
      
      {/* ERROR DISPLAY (Bottom of page, auto-dismiss) */}
      {fetchError && (
        <div className={styles.alertError}>
           <p>{fetchError}</p>
        </div>
      )}

      {selectedListing && (
        <AnalyticsPanel
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Analytics Panel ---------------- */

function AnalyticsPanel({ listing, onClose }) {
  const [totals, setTotals] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("post_id", listing.pageLink);
    if (listing.authorId) {
      params.set("author_id", listing.authorId);
    }
    params.set("_ts", Date.now().toString());

    setIsLoading(true);
    setError(null);

    fetch(`${BASE_URL}backend/get_listing_totals.php?${params.toString()}`)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
        const text = await r.text();
        try {
          return JSON.parse(text);
        } catch (parseErr) {
          console.error("JSON Parse Error. Response text:", text);
          throw new Error("Invalid JSON response from server");
        }
      })
      .then((data) => {
        if (data.ok === false) {
          setError(data.error || "Unknown error from server");
          setTotals(null);
        } else {
          setTotals(data);
        }
      })
      .catch((err) => {
        console.error("Error fetching listing totals:", err);
        setError(err.message || "Failed to load analytics");
        setTotals(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [listing.pageLink, listing.authorId]);

  return (
    <div className={styles.analyticsSheet} onClick={onClose}>
      <div className={styles.sheetContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.sheetHeader}>
          <div className={styles.sheetTitleBlock}>
            {listing.title && (
              <h3 className={styles.sheetListingTitle}>{listing.title}</h3>
            )}
            <h2 className={styles.sheetTitleBig}>
              {listing.bedrooms} bed, {listing.bathrooms} bath apartment
            </h2>
            <div className={styles.sheetSubInfo}>
              <span className={styles.sheetPrice}>${listing.price}/month</span>
              <span className={styles.dot}>•</span>
              <span className={styles.sheetAddress}>{listing.address}</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.sheetBody}>
          {isLoading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>Loading analytics...</div>
          ) : error ? (
            <div style={{ padding: "20px", color: "#e53e3e" }}>
              <strong>Error:</strong> {error}
              {!listing.authorId && (
                <div style={{ marginTop: "10px", fontSize: "14px" }}>
                  Note: Author ID is missing for this listing
                </div>
              )}
            </div>
          ) : (
            <div className={styles.cardGrid3}>
              <MiniStat icon={clickIcon} label="Listing Clicks" value={totals?.listing_clicks ?? 0} />
              <MiniStat icon={commentsIcon} label="Comments" value={totals?.comments ?? 0} />
              <MiniStat icon={viewsIcon} label="Profile Views" value={totals?.profile_clicks ?? 0} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (  
    <div className={styles.miniStat}>
      <div className={styles.miniLeft}>
        <img src={icon} alt="" className={styles.miniIcon} />
        <div className={styles.miniLabel}>{label}</div>
      </div>
      <div className={styles.miniValue}>{value}</div>
    </div>
  );
}